import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Skeleton } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { SaveParams } from 'utils';
import { ManageActions } from '../tabs';

/**
 * Active-filter chips for a list grid.
 *
 * Callers describe their filters declaratively and this owns everything else — reading
 * `location.state`, building one chip per value, removing one, clearing them all, resetting paging
 * and writing back through `SaveParams`. Nine grids had hand-written copies of that same
 * `activeFilters` / `removeFilter` / `handleClearAll` trio; the only parts that genuinely differed
 * were which keys exist and how to label them, which is exactly what `filtersList` is.
 *
 * A descriptor:
 *
 *   {
 *     name,        // chip prefix — "Status" renders `Status: Active`
 *     label,       // fixed full label instead of `name: value`, for boolean flags ("w/o Notes")
 *     key,         // the `location.state` key holding the value (or array of values)
 *     keys,        // ...or several read together, e.g. ['from','to'] for a date range
 *     pairKey,     // a parallel array cleared by the same id — ids alongside objects
 *     companions,  // extra keys removed with this filter, never charted on their own
 *     options,     // [{ id, name }] to resolve a value's label
 *     loading,     // declared only by fetched catalogs — see "Unresolved values" below
 *     unknownName, // label for an id a settled `options` has no entry for, default "Unknown"
 *     render,      // (value, info) => label — full control; wins over `options`
 *     skipValues,  // values that mean "no filter", e.g. a column's own 'ALL' option
 *     show,        // false hides the chip; "Clear Filters" still clears the key
 *     defaultValue,// the selection the grid applies while `key` is absent — see "Defaults" below
 *   }
 *
 * An array value yields one chip per entry, each removable on its own; a scalar yields one. A key
 * that is absent, empty, `false` or in `skipValues` yields none.
 *
 * ## Defaults
 *
 * Clients and Staff never load unfiltered: while `statuses` is absent their `renderParams` sends a
 * default set, so a fresh grid hides Terminated and Graduated rows with nothing on screen saying so
 * — the Status column shows its boxes ticked, but only if you open it. `defaultValue` is that set
 * declared to this bar, which then charts it like any other selection.
 *
 * Because those chips stand for a value that is not in `location.state` yet, the two write paths
 * materialise it rather than deleting the key:
 *
 *   - removing one chip writes the rest of the default set explicitly, exactly as unticking that box
 *     in the column does — deleting the key would re-apply the default and the chip would come back;
 *   - removing the last chip, and "Clear Filters", write `[]` — the column's own "All", which
 *     `renderParams` strips from the request. Present and narrowing nothing, so the default stays
 *     off until the user asks for it again.
 *
 * `show` hides a chip without un-declaring the filter, for tab-scoped grids: a filter that the
 * visible tab does not apply must not show a chip, but "Clear Filters" must still clear it or it
 * survives invisibly into the tab that does apply it.
 *
 * ## Unresolved values
 *
 * `location.state` survives a reload (the browser keeps `history.state`) but the catalogs that name
 * its ids — tags, domains — are fetched on mount, so for the first few hundred milliseconds a chip
 * holds an id it cannot label. Printing it raw is how "Tag: 68f1a9c2e4..." flashed on every refresh.
 *
 * A descriptor whose catalog is fetched says so with `loading` — `loading: !tagList?.length` is the
 * usual form — and its ids are then never printed. While it is true the chip is a skeleton the size
 * of a chip, and settles into the name when the catalog lands; once it is false and the id still has
 * no entry — a tag deleted out from under a saved filter — the chip reads "Tag: Unknown", which is
 * both true and still removable.
 *
 * A descriptor that omits `loading` keeps printing the value itself when `options` has no entry for
 * it. That is deliberate: several of the static enum catalogs are partial by design — entries sit
 * commented out of `scheduleStatusEnums` — and there "Status: RENDERED" beats "Status: Unknown".
 */

/** A value may be a bare id or an object carrying one; `renderParams` normalises both the same way. */
const valueId = (item) => item?.id ?? item;

const isEmpty = (value) => value === undefined || value === null || value === false || value === '';

/** Every state key one descriptor owns — what it charts, its paired ids, and its companions. */
const keysOf = (filter) => [...(filter.keys || [filter.key]), ...(filter.pairKey ? [filter.pairKey] : []), ...(filter.companions || [])];

/** A value whose catalog has not arrived yet: the chip is drawn as a skeleton instead. */
const PENDING = Symbol('pending');

const labelFor = (filter, value, info) => {
    if (filter.render) return filter.render(value, info);
    const id = valueId(value);
    const match = filter.options?.find((i) => i?.id === id);
    if (match) return match.name;
    // No `loading`: a static catalog (or none at all), where the value labels itself.
    if (filter.loading === undefined) return id;
    return filter.loading ? PENDING : filter.unknownName || 'Unknown';
};

/** One chip's text, or `PENDING` when its value cannot be named yet. */
const chipLabel = (filter, value, info) => {
    if (filter.label) return filter.label;
    const label = labelFor(filter, value, info);
    return label === PENDING ? PENDING : `${filter.name}: ${label}`;
};

/** Shared shape for one chip. A pending one carries no label — it is drawn as a placeholder. */
const chip = (filter, label, { id, key = filter.key } = {}) => (label === PENDING ? { pending: true, key, id } : { label, key, id });

/** Chips for one descriptor: none, one, or one per array entry. */
const chipsFor = (filter, info) => {
    if (filter.show === false) return [];

    // A multi-key filter (a date range) is one chip over several keys, shown when any is set.
    if (filter.keys) {
        const values = filter.keys.map((k) => info?.[k]);
        if (values.every(isEmpty)) return [];
        return [chip(filter, chipLabel(filter, values, info), { key: filter.key || filter.keys[0] })];
    }

    // Only `undefined`/`null` fall back to the default: `[]` is a real, empty selection — the
    // column's "All" — and must not be read as "no choice made, show the default".
    const value = info?.[filter.key] ?? filter.defaultValue;
    if (isEmpty(value)) return [];
    if (filter.skipValues?.includes(value)) return [];

    if (Array.isArray(value)) {
        return value.map((entry) => chip(filter, chipLabel(filter, entry, info), { id: valueId(entry) }));
    }

    // A boolean flag carries no value worth printing — `label` is the whole chip.
    return [chip(filter, chipLabel(filter, value, info))];
};

/** A whole chip's worth of placeholder — same height as a real one, so nothing shifts when it lands. */
const PendingChip = () => <Skeleton variant="rounded" className="selected-filter-chip-skeleton" width={112} height={26} />;

const FilterChip = ({ filter, onRemove, crop }) =>
    filter.pending ? (
        <PendingChip />
    ) : (
        <div className={`selected-filter-chip${crop ? ' is-cropped' : ''}`}>
            <span>{filter.label}</span>
            <CloseIcon onClick={() => onRemove(filter.key, filter.id)} />
        </div>
    );

export const SelectedFilters = ({
    /**
     * The grid's filters, declared. Chips, removal and "Clear Filters" are all derived from this,
     * so the three can no longer disagree — which is what made a stale `handleClearAll` (a chip that
     * came back, or a filter that survived a clear) the recurring bug in the hand-written versions.
     */
    filtersList = [],
    /** Merged into `location.state` on every change. A filter change invalidates the current page. */
    resetParams = { skip: 0, page: 1 },
    /**
     * Side effects a grid needs on top of the state write — Claims and Claim Payments keep a
     * sessionStorage snapshot in step. Receives the params about to be saved and the keys removed.
     */
    onChange,
    maxVisible = 3,
    width,
    doNotCrop = false,
    // 0 when the bar shares a row with other content (ScheduleInformation) — the default
    // spacing is for the case where it sits on its own row above the content it filters.
    marginBottom = 12,
    /**
     * Controlled mode, for filters that live in component state instead of `location.state` — a
     * drawer or modal (the Clients & Staff Map) must not rewrite the page it was opened over. Pass
     * both: `state` is read in place of `location.state`, `onSave(params)` replaces `SaveParams`.
     */
    state,
    onSave,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const info = onSave ? state : location?.state;

    const activeFilters = filtersList.flatMap((filter) => chipsFor(filter, info));

    const save = (params, removedKeys) => {
        onChange?.(params, { removedKeys });
        if (onSave) onSave(params);
        else SaveParams(navigate, params);
    };

    /** Drop every key a descriptor owns — or, for a default-bearing one, park it on an empty selection. */
    const clearFilter = (params, filter) => {
        keysOf(filter).forEach((k) => delete params[k]);
        if (filter.defaultValue !== undefined) params[filter.key] = [];
    };

    const handleRemove = (key, id) => {
        const filter = filtersList.find((f) => (f.key || f.keys?.[0]) === key);
        if (!filter) return;
        const params = { ...info, ...resetParams };
        const removedKeys = keysOf(filter);

        // The chip being removed may be one of a default set that was never written to state. Write
        // it out first so the entry is removed from a real selection instead of from nothing.
        if (filter.defaultValue !== undefined && params[filter.key] == null) {
            params[filter.key] = filter.defaultValue;
        }

        if (id !== undefined && Array.isArray(params[filter.key])) {
            // One entry of a multi-select. Drop it from the charted array and from any parallel id
            // array, then clear the whole filter only once nothing is left selected.
            params[filter.key] = params[filter.key].filter((item) => valueId(item) !== id);
            if (filter.pairKey && Array.isArray(params[filter.pairKey])) {
                params[filter.pairKey] = params[filter.pairKey].filter((item) => valueId(item) !== id);
            }
            if (params[filter.key].length) {
                (filter.companions || []).forEach((k) => delete params[k]);
                save(params, filter.companions || []);
                return;
            }
        }

        clearFilter(params, filter);
        save(params, removedKeys);
    };

    const handleClearAll = () => {
        const params = { ...info, ...resetParams };
        const removedKeys = filtersList.flatMap(keysOf);
        filtersList.forEach((filter) => clearFilter(params, filter));
        save(params, removedKeys);
    };

    if (!activeFilters.length) return null;

    const visibleFilters = doNotCrop ? activeFilters : activeFilters.slice(0, maxVisible);
    const hiddenCount = doNotCrop ? 0 : Math.max(0, activeFilters.length - maxVisible);

    return (
        <div
            className="selected-filters-wrapper flex-align-center"
            style={{ gap: 8, marginBottom, ...(width ? { width: typeof width === 'number' ? `${width}px` : width } : {}) }}
        >
            {visibleFilters.map((f, i) => (
                <FilterChip key={i} filter={f} onRemove={handleRemove} crop={!doNotCrop} />
            ))}

            {hiddenCount > 0 && (
                <ManageActions
                    btnClass="selected-filter-chip hidden-count-chip"
                    btnTitle={`+${hiddenCount}`}
                    renderBody={() => (
                        // The popover sizes to its own content, so overflow chips always
                        // show their full label regardless of how the bar itself is bounded.
                        <div className="selected-filters-wrapper flex-column-start" style={{ gap: 6 }}>
                            {activeFilters.slice(maxVisible).map((f, i) => (
                                <FilterChip key={i} filter={f} onRemove={handleRemove} />
                            ))}
                        </div>
                    )}
                />
            )}

            <button className="clear-filters-text-btn" onClick={handleClearAll}>
                Clear Filters
            </button>
        </div>
    );
};
